import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  Link,
  InputLabel,
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
  ListItemText,
  Autocomplete,
  CircularProgress,
  Switch,
} from '@mui/material';
import type { AutocompleteInputChangeReason } from '@mui/material/useAutocomplete';
import { observer } from 'mobx-react-lite';
import styleVariables from '../../scss/_variables.scss';
import {
  DialogFor,
  useDialogStore,
} from '../../stores/DialogStore/DialogStore';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATHS } from 'src/constants/navigateRoutes';
import { CustomDivider } from '../custom-divider/CustomDivider';
import CustomLoading from '../custom-loading';
import dayjs, { Dayjs } from 'dayjs';
import styles from './CustomDialog.module.scss';
import { SPACE_ID_REGEX } from 'src/constants/Regex';
import { GOOGLE_CHAT_SPACES_PREFIX } from 'src/constants/googleChat';
import {
  AUTOCOMPLETE_LOADING_INDICATOR_SIZE,
  getTextFieldStyles,
} from 'src/constants/Layout';
import { useOrganizationStore } from 'src/stores/OrganizationStore/OrganizationStore';
import { PLAN_TYPES } from 'src/constants/cardsEnum';
import { isOrganizationPlanAtLeast } from 'src/utils/organizationBilling';
import { isString } from 'formik';
import { Tag } from 'src/types/tag';
import {
  AUTOCOMPLETE_INPUT_CHANGE_REASON_CLEAR,
  AUTOCOMPLETE_INPUT_CHANGE_REASON_INPUT,
} from 'src/constants/spaces';
import {
  DateTimePicker,
  DatePicker,
  DateTimeValidationError,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { EMPTY_STRING, ZERO } from 'src/constants/utils';
import { CustomTooltip } from '../custom-tooltip/CustomTooltip';
import { useTranslation } from 'react-i18next';
import TagSelect from '../custom-kanban/TagSelect';

export interface FormFieldOption {
  label: string;
  value: string;
  description?: string;
}

export interface FormField {
  label: string;
  id: string;
  value: string | boolean | number | Tag[];
  type: string;
  maxLength?: number;
  helperText?: React.ReactNode;
  errorText?: React.ReactNode;
  required?: boolean;
  requiredErrorText?: React.ReactNode;
  validate?: (
    value: string | boolean | number | Tag[],
    fields: FormField[],
  ) => React.ReactNode | null;
  fieldHeading?: string;
  toolTipTitle?: string;
  toolTipDescription?: string;
  toolTipPlacement?:
    | 'bottom-end'
    | 'bottom-start'
    | 'bottom'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top';
  // Optional styles for the field wrapper container
  // TODO: Refactor to a shared type once dialog field system is generalized
  containerSx?: Record<string, unknown>;
  minDateTimeInMinute?: number;
  dateErrorMessage?: {
    [key in Exclude<DateTimeValidationError, null>]?: React.ReactNode;
  };
  handleDateTimeError?: (error: DateTimeValidationError | null) => void;
  options?: FormFieldOption[];
  multiSelectValue?: string[];
  placeholder?: string;
  disabled?: boolean;
  onValueChange?: (
    value: string | boolean | number | Tag[],
  ) => Partial<FormField>;
  onInputChange?: (value: string) => void;
  noOptionsText?: string;
  isLoading?: boolean;
  isAdvanced?: boolean;
}

const passThroughFormFieldOptions = (options: FormFieldOption[]) => options;

const CustomDialog = observer(() => {
  const { t } = useTranslation();
  const {
    dialogueState,
    dialogFormFields,
    updateDialogMetaData,
    dialogTitle,
    dialogContent,
    dialogType,
    dialogStyle,
    updateDialogueState,
    isSubmitDisabled,
    resetDialog,
  } = useDialogStore();

  const navigate = useNavigate();
  const { organizationDetails } = useOrganizationStore();
  const isProPlan = isOrganizationPlanAtLeast(
    organizationDetails,
    PLAN_TYPES.PRO,
  );

  const handleChange = (fieldId: string, value: FormField['value']) => {
    if (updateDialogMetaData && dialogType) {
      const updatedFields = dialogFormFields.map((field) => {
        if (field.id !== fieldId) {
          return field;
        }

        let updatedValue: string | boolean | number | Tag[] = value;

        if (field.type === 'number' && isString(value)) {
          updatedValue = value === EMPTY_STRING ? EMPTY_STRING : Number(value);
        }

        if (field.id === 'spaceName' && isString(updatedValue)) {
          updatedValue = updatedValue.replace(
            SPACE_ID_REGEX,
            GOOGLE_CHAT_SPACES_PREFIX,
          );
        }

        const extraProps = field.onValueChange
          ? field.onValueChange(updatedValue)
          : {};

        return {
          ...field,
          errorText: undefined,
          value: extraProps.value ?? updatedValue,
          ...extraProps,
        };
      });

      updateDialogMetaData(
        dialogTitle,
        dialogContent,
        dialogType,
        updatedFields,
        dialogStyle,
      );
    }
  };

  const handleMultiSelectChange = (
    fieldId: string,
    value: string | string[],
  ) => {
    if (updateDialogMetaData && dialogType) {
      const updatedFields = dialogFormFields.map((field) =>
        field.id === fieldId
          ? {
              ...field,
              errorText: undefined,
              multiSelectValue:
                typeof value === 'string' ? value.split(',') : value,
            }
          : field,
      );

      updateDialogMetaData(
        dialogTitle,
        dialogContent,
        dialogType,
        updatedFields,
        dialogStyle,
      );
    }
  };

  const handleDialogClose = () => {
    resetDialog();
  };

  const handleUpgrade = () => {
    navigate(PATHS.pricingPlans);
  };

  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    setShowAdvanced(false);
  }, [dialogType]);

  if (dialogueState === 'CLOSED') {
    return null;
  }

  const hasAdvancedFields = dialogFormFields.some((field) => field.isAdvanced);

  const getVisibleDialogFields = () =>
    dialogFormFields.filter((field) => !field.isAdvanced || showAdvanced);

  const isFieldValueEmpty = (field: FormField) => {
    if (field.type === 'tags') {
      return !Array.isArray(field.value) || field.value.length === 0;
    }

    if (field.type === 'multi-select') {
      return (
        !Array.isArray(field.multiSelectValue) ||
        field.multiSelectValue.length === 0
      );
    }

    if (field.type === 'checkbox') {
      return !Boolean(field.value);
    }

    if (typeof field.value === 'string') {
      return field.value.trim().length === 0;
    }

    if (typeof field.value === 'number') {
      return Number.isNaN(field.value);
    }

    return !field.value;
  };

  const getRequiredErrorText = (field: FormField): React.ReactNode => {
    if (field.requiredErrorText) {
      return field.requiredErrorText;
    }

    return `${field.fieldHeading ?? field.label} is required.`;
  };

  const focusFirstErrorField = (fieldId: string) => {
    window.requestAnimationFrame(() => {
      const fieldElement = document.getElementById(fieldId);

      if (!fieldElement) {
        return;
      }

      fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

      if (fieldElement instanceof HTMLElement) {
        fieldElement.focus({ preventScroll: true });
      }
    });
  };

  const validateDialogFields = (): boolean => {
    if (!updateDialogMetaData || !dialogType) {
      return true;
    }

    const visibleFields = getVisibleDialogFields();
    const visibleFieldIds = new Set(visibleFields.map((field) => field.id));
    const fieldErrorsById = new Map<string, React.ReactNode>();

    for (const field of visibleFields) {
      if (field.required && isFieldValueEmpty(field)) {
        fieldErrorsById.set(field.id, getRequiredErrorText(field));
        continue;
      }

      if (field.validate) {
        const validationMessage = field.validate(field.value, visibleFields);

        if (validationMessage) {
          fieldErrorsById.set(field.id, validationMessage);
        }
      }
    }

    const updatedFields = dialogFormFields.map((field) => {
      if (!visibleFieldIds.has(field.id)) {
        return {
          ...field,
          errorText: undefined,
        };
      }

      return {
        ...field,
        errorText: fieldErrorsById.get(field.id),
      };
    });

    updateDialogMetaData(
      dialogTitle,
      dialogContent,
      dialogType,
      updatedFields,
      dialogStyle,
    );

    const firstErroredFieldId = visibleFields.find((field) =>
      fieldErrorsById.has(field.id),
    )?.id;

    if (firstErroredFieldId) {
      focusFirstErrorField(firstErroredFieldId);

      return false;
    }

    return true;
  };

  const getFieldLabelNode = (field: FormField, label: string) => {
    if (!field.toolTipDescription && !field.toolTipTitle) {
      return label;
    }

    return (
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
      >
        <Typography component="span" variant="inherit">
          {label}
        </Typography>
        <CustomTooltip
          clickId={`${field.id}-tooltip`}
          toolTipTitle={field.toolTipTitle ?? label}
          toolTipDescription={field.toolTipDescription}
          placement={field.toolTipPlacement ?? 'top'}
        />
      </Box>
    );
  };

  const getReferenceLink = (field: FormField) => {
    const referenceUrl = field.value ? field.value : window.location.pathname;
    const referenceLabel = field.value ? field.label : EMPTY_STRING;

    return (
      <Link
        id={field.id}
        key={field.id}
        href={referenceUrl.toString()}
        referrerPolicy="no-referrer"
        target="_blank"
        sx={{
          width: 'fit-content',
        }}
      >
        {referenceLabel.toString()}
      </Link>
    );
  };

  const getDialogDateTimeFormField = (field: FormField) => {
    const fiveMinutesFromNow = dayjs().add(
      field.minDateTimeInMinute ?? ZERO,
      'minutes',
    );

    const dateErrorMessage = field.dateErrorMessage
      ? Object.values(field.dateErrorMessage).join(' ')
      : EMPTY_STRING;
    const helperText = field.errorText ?? dateErrorMessage;
    const hasError = Boolean(field.errorText || dateErrorMessage);

    const DATE_TIME_STYLES = getTextFieldStyles(field);

    return (
      <Box key={field.id} sx={field.containerSx}>
        <Typography
          sx={{
            marginTop: '8px',
            marginBottom: '4px',
            fontSize: '20px',
            textTransform: 'capitalize',
          }}
          component="p"
        >
          {getFieldLabelNode(field, field.fieldHeading ?? field.label)}
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            formatDensity="dense"
            label={getFieldLabelNode(field, field.fieldHeading ?? field.label)}
            orientation="portrait"
            value={
              field.value && isString(field.value) ? dayjs(field.value) : null
            }
            onChange={(newValue: Dayjs | null) => {
              const formattedValue = newValue
                ? newValue.toISOString()
                : EMPTY_STRING;

              handleChange(field.id, formattedValue);
            }}
            minDateTime={fiveMinutesFromNow}
            onError={(newError) => {
              if (field.handleDateTimeError) {
                field.handleDateTimeError(newError);
              }
            }}
            slotProps={{
              textField: {
                id: field.id,
                helperText,
                error: hasError,
              },
            }}
            disabled={field.disabled}
            sx={DATE_TIME_STYLES}
          />
        </LocalizationProvider>
      </Box>
    );
  };

  const getDialogDateFormField = (field: FormField) => {
    const DATE_STYLES = getTextFieldStyles(field);
    const hasError = Boolean(field.errorText);

    return (
      <Box key={field.id} sx={field.containerSx}>
        <Typography
          sx={{
            marginTop: '8px',
            marginBottom: '4px',
            fontSize: '20px',
            textTransform: 'capitalize',
          }}
          component="p"
        >
          {getFieldLabelNode(field, field.fieldHeading ?? field.label)}
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={getFieldLabelNode(field, field.fieldHeading ?? field.label)}
            value={
              field.value && isString(field.value) ? dayjs(field.value) : null
            }
            onChange={(newValue: Dayjs | null) => {
              const formattedValue = newValue
                ? newValue.toISOString()
                : EMPTY_STRING;

              handleChange(field.id, formattedValue);
            }}
            slotProps={{
              textField: {
                id: field.id,
                helperText: field.errorText ?? field.helperText,
                error: hasError,
              },
            }}
            disabled={field.disabled}
            sx={DATE_STYLES}
          />
        </LocalizationProvider>
      </Box>
    );
  };

  const getDialogCheckBoxFormField = (field: FormField) => {
    const hasError = Boolean(field.errorText);

    return (
      <Box sx={{ marginTop: 2, ...(field.containerSx || {}) }} key={field.id}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(field.value)}
              onChange={(e) => handleChange(field.id, e.target.checked)}
              disabled={field.disabled}
            />
          }
          label={
            <Typography component="p">
              {getFieldLabelNode(field, field.label)}
            </Typography>
          }
        />
        {hasError && (
          <FormHelperText error sx={{ marginLeft: 0 }}>
            {field.errorText}
          </FormHelperText>
        )}
      </Box>
    );
  };

  const getDialogMultipleSelectFormField = (field: FormField) => {
    if (!Array.isArray(field.multiSelectValue) || !field.multiSelectValue) {
      return 'Value must be an array';
    }

    if (field.onInputChange) {
      const selectedOptionsFromList = (field.options ?? []).filter((option) =>
        field.multiSelectValue?.includes(option.value),
      );
      const fallbackSelectedOptions = field.multiSelectValue
        .filter(
          (value) =>
            !selectedOptionsFromList.some((option) => option.value === value),
        )
        .map((value) => ({
          label: value,
          value,
          description: value,
        }));
      const selectedOptions = [
        ...selectedOptionsFromList,
        ...fallbackSelectedOptions,
      ];

      const handleMultiSelectAutocompleteChange = (
        _event: React.SyntheticEvent,
        options: FormFieldOption[],
      ) => {
        handleMultiSelectChange(
          field.id,
          options.map((option) => option.value),
        );
      };

      const handleMultiSelectAutocompleteInputChange = (
        _event: React.SyntheticEvent,
        value: string,
        reason: AutocompleteInputChangeReason,
      ) => {
        if (reason === AUTOCOMPLETE_INPUT_CHANGE_REASON_CLEAR) {
          field.onInputChange?.(EMPTY_STRING);

          return;
        }

        if (reason === AUTOCOMPLETE_INPUT_CHANGE_REASON_INPUT) {
          field.onInputChange?.(value);
        }
      };

      return (
        <Box key={field.id} sx={field.containerSx}>
          {field.fieldHeading && (
            <Typography
              sx={{
                marginTop: '8px',
                marginBottom: '4px',
                textTransform: 'capitalize',
              }}
              component="p"
              variant="body1"
            >
              {getFieldLabelNode(field, field.fieldHeading)}
            </Typography>
          )}
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={field.options ?? []}
            value={selectedOptions}
            onChange={handleMultiSelectAutocompleteChange}
            onInputChange={handleMultiSelectAutocompleteInputChange}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            filterOptions={passThroughFormFieldOptions}
            noOptionsText={field.noOptionsText}
            loading={Boolean(field.isLoading)}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.value}>
                <Checkbox checked={selected} />
                <ListItemText
                  primary={option.label}
                  secondary={option.description}
                />
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={getFieldLabelNode(
                  field,
                  field.fieldHeading ?? field.label,
                )}
                id={field.id}
                placeholder={field.placeholder ?? field.label}
                helperText={field.errorText ?? field.helperText}
                error={Boolean(field.errorText)}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {field.isLoading && (
                        <CircularProgress
                          size={AUTOCOMPLETE_LOADING_INDICATOR_SIZE}
                        />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            disabled={field.disabled}
          />
        </Box>
      );
    }

    const optionLabelByValue = new Map(
      (field.options ?? []).map(({ value, label }) => [value, label]),
    );

    return (
      <Box key={field.id} sx={field.containerSx}>
        {field.fieldHeading && (
          <Typography
            sx={{
              marginTop: '8px',
              marginBottom: '4px',
              textTransform: 'capitalize',
            }}
            component="p"
            variant="body1"
          >
            {getFieldLabelNode(field, field.fieldHeading)}
          </Typography>
        )}
        <FormControl
          sx={{ mb: 8, width: '100%' }}
          error={Boolean(field.errorText)}
        >
          <InputLabel id={`${field.id}-label`}>
            {getFieldLabelNode(field, field.fieldHeading ?? field.label)}
          </InputLabel>
          <Select
            labelId={`${field.id}-label`}
            id={field.id}
            multiple
            value={field.multiSelectValue}
            onChange={(e) => handleMultiSelectChange(field.id, e.target.value)}
            renderValue={(selected) =>
              selected
                .map((value) => optionLabelByValue.get(value) ?? value)
                .join(', ')
            }
            label={getFieldLabelNode(field, field.fieldHeading ?? field.label)}
            disabled={field.disabled}
          >
            {field.options?.map(({ label, value, description }) => (
              <MenuItem key={label} value={value}>
                <Checkbox
                  checked={field.multiSelectValue?.includes(value.toString())}
                />
                <ListItemText primary={label} secondary={description} />
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{field.errorText ?? field.helperText}</FormHelperText>
        </FormControl>
      </Box>
    );
  };

  const getDialogSelectFormField = (field: FormField) => {
    return (
      <Box key={field.id} sx={field.containerSx}>
        {field.fieldHeading && (
          <Typography
            sx={{
              marginTop: '8px',
              marginBottom: '4px',
              textTransform: 'capitalize',
            }}
            component="p"
            variant="body1"
          >
            {getFieldLabelNode(field, field.fieldHeading)}
          </Typography>
        )}

        <FormControl
          sx={{ mb: 8, width: '100%' }}
          error={Boolean(field.errorText)}
        >
          <InputLabel id={`${field.id}-label`}>
            {getFieldLabelNode(field, field.fieldHeading ?? field.label)}
          </InputLabel>
          <Select
            labelId={`${field.id}-label`}
            id={field.id}
            value={field.value}
            onChange={(e) => handleChange(field.id, e.target.value)}
            label={getFieldLabelNode(field, field.fieldHeading ?? field.label)}
            disabled={field.disabled}
          >
            {field.options?.map(({ label, value, description }) => (
              <MenuItem key={label} value={value}>
                <ListItemText primary={label} secondary={description} />
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{field.errorText ?? field.helperText}</FormHelperText>
        </FormControl>
      </Box>
    );
  };

  const getDialogAutocompleteFormField = (field: FormField) => {
    const selectedOptionFromList =
      field.options?.find((option) => option.value === field.value) ?? null;

    const fallbackOption =
      !selectedOptionFromList &&
      isString(field.value) &&
      field.value !== EMPTY_STRING
        ? {
            label: field.value,
            value: field.value,
            description: field.value,
          }
        : null;

    const selectedOption = selectedOptionFromList ?? fallbackOption;

    const handleOptionChange = (
      _event: React.SyntheticEvent,
      option: FormFieldOption | null,
    ) => {
      handleChange(field.id, option?.value ?? EMPTY_STRING);
    };

    const handleInputValueChange = (
      _event: React.SyntheticEvent,
      value: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      if (!field.onInputChange) {
        return;
      }

      if (reason === AUTOCOMPLETE_INPUT_CHANGE_REASON_CLEAR) {
        field.onInputChange(EMPTY_STRING);

        return;
      }

      if (reason === AUTOCOMPLETE_INPUT_CHANGE_REASON_INPUT) {
        field.onInputChange(value);
      }
    };

    return (
      <Box key={field.id} sx={field.containerSx}>
        {field.fieldHeading && (
          <Typography
            sx={{
              marginTop: '8px',
              marginBottom: '4px',
              textTransform: 'capitalize',
            }}
            component="p"
            variant="body1"
          >
            {getFieldLabelNode(field, field.fieldHeading)}
          </Typography>
        )}
        <Autocomplete
          options={field.options ?? []}
          value={selectedOption}
          onChange={handleOptionChange}
          onInputChange={handleInputValueChange}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          filterOptions={passThroughFormFieldOptions}
          noOptionsText={field.noOptionsText}
          loading={Boolean(field.isLoading)}
          renderOption={(props, option) => (
            <li {...props} key={option.value}>
              <ListItemText
                primary={option.label}
                secondary={option.description}
              />
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={getFieldLabelNode(
                field,
                field.fieldHeading ?? field.label,
              )}
              id={field.id}
              placeholder={field.placeholder ?? field.label}
              helperText={field.errorText ?? field.helperText}
              error={Boolean(field.errorText)}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {field.isLoading && (
                      <CircularProgress
                        size={AUTOCOMPLETE_LOADING_INDICATOR_SIZE}
                      />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          disabled={field.disabled}
        />
      </Box>
    );
  };

  const getDialogColorFormField = (field: FormField) => {
    return (
      <Box key={field.id}>
        {field.fieldHeading && (
          <Typography
            sx={{
              marginTop: '8px',
              marginBottom: '4px',
              textTransform: 'capitalize',
            }}
            component="p"
            variant="body1"
          >
            {getFieldLabelNode(field, field.fieldHeading)}
          </Typography>
        )}

        <TextField
          key={field.id}
          margin="dense"
          id={field.id}
          label={getFieldLabelNode(field, field.fieldHeading ?? field.label)}
          value={field.value || styleVariables.colorBlack}
          type="color"
          fullWidth
          variant="outlined"
          onChange={(e) => handleChange(field.id, e.target.value)}
          disabled={field.disabled}
        />
      </Box>
    );
  };

  const getDialogTagsFormField = (field: FormField) => {
    const tagValue = Array.isArray(field.value) ? field.value : [];

    return (
      <Box key={field.id} sx={field.containerSx}>
        {field.fieldHeading && (
          <Typography
            sx={{
              marginTop: '8px',
              marginBottom: '4px',
              textTransform: 'capitalize',
            }}
            component="p"
            variant="body1"
          >
            {getFieldLabelNode(field, field.fieldHeading)}
          </Typography>
        )}
        <TagSelect
          value={tagValue}
          onChange={(tags) => handleChange(field.id, tags)}
        />
        {(field.errorText || field.helperText) && (
          <Typography
            sx={{ fontSize: '12px', mt: 1 }}
            variant="body2"
            color={field.errorText ? 'error.main' : undefined}
            className={field.errorText ? undefined : styles.generalHelperText}
          >
            {field.errorText ?? field.helperText}
          </Typography>
        )}
      </Box>
    );
  };

  const getDialogTextFormField = (field: FormField) => {
    const TEXT_FIELD_STYLES = getTextFieldStyles(field);
    const hasError = Boolean(field.errorText);
    const helperContent = field.errorText ?? field.helperText;

    return (
      <Box
        key={field.id}
        sx={{
          ...(field.type === 'textarea' && {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
          }),
          ...(field.containerSx || {}),
        }}
      >
        {field.fieldHeading && (
          <Typography
            sx={{
              marginTop: '8px',
              marginBottom: '4px',
              textTransform: 'capitalize',
            }}
            component="p"
            variant="body1"
          >
            {getFieldLabelNode(field, field.fieldHeading)}
          </Typography>
        )}

        <TextField
          key={field.id}
          margin="dense"
          id={field.id}
          label={getFieldLabelNode(field, field.fieldHeading ?? field.label)}
          value={field.value || EMPTY_STRING}
          placeholder={field.placeholder ?? field.label}
          type={field.type}
          fullWidth
          variant="outlined"
          onChange={(e) => handleChange(field.id, e.target.value)}
          disabled={field.disabled}
          multiline={field.type === 'textarea'}
          inputProps={{ maxLength: field.maxLength }}
          InputProps={{
            style: TEXT_FIELD_STYLES.textareaInput,
          }}
          error={hasError}
          helperText={
            field.maxLength && isString(field.value) ? (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography
                  variant="body2"
                  component="span"
                  className={hasError ? undefined : styles.generalHelperText}
                  sx={hasError ? { color: 'error.main' } : undefined}
                >
                  {helperContent}
                </Typography>
                <Typography
                  variant="body2"
                  className={styles.maxLengthHelperText}
                >
                  {field.value.length}/{field.maxLength}
                </Typography>
              </Box>
            ) : (
              <Typography
                sx={{
                  fontSize: '12px',
                }}
                variant="body2"
                component="span"
                className={hasError ? undefined : styles.generalHelperText}
                color={hasError ? 'error.main' : undefined}
              >
                {helperContent}
              </Typography>
            )
          }
          sx={{ flex: field.type === 'textarea' ? 1 : EMPTY_STRING }}
        />
      </Box>
    );
  };

  const getDialogFormField = (field: FormField) => {
    switch (field.type) {
      case 'url':
        return getReferenceLink(field);

      case 'date-time':
        return getDialogDateTimeFormField(field);

      case 'date':
        return getDialogDateFormField(field);

      case 'checkbox':
        return getDialogCheckBoxFormField(field);

      case 'multi-select':
        return getDialogMultipleSelectFormField(field);

      case 'select':
        return getDialogSelectFormField(field);

      case 'autocomplete':
        return getDialogAutocompleteFormField(field);

      case 'color':
        return getDialogColorFormField(field);
      case 'tags':
        return getDialogTagsFormField(field);

      default:
        return getDialogTextFormField(field);
    }
  };

  const getDialogueContent = () => {
    if (!dialogType) {
      return (
        <DialogContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DialogContentText>
            <Typography component="span">Invalid dialogue type</Typography>
          </DialogContentText>
        </DialogContent>
      );
    }

    if (dialogueState === 'LOADING') {
      return (
        <DialogContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CustomLoading />
        </DialogContent>
      );
    }

    return (
      <>
        <DialogContent
          sx={{
            padding: '0 24px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DialogContentText sx={{ marginBottom: '16px' }}>
            {typeof dialogContent === 'string' ? (
              <Typography component="span">{t(dialogContent)}</Typography>
            ) : (
              dialogContent
            )}
          </DialogContentText>
          {hasAdvancedFields && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={showAdvanced}
                    onChange={(event) => setShowAdvanced(event.target.checked)}
                  />
                }
                label={showAdvanced ? t('hide_advanced') : t('show_advanced')}
              />
              <CustomTooltip
                clickId="dialog-advanced-toggle"
                toolTipTitle={t('advanced_options')}
                toolTipDescription={t('show_advanced_tooltip')}
                placement="top"
              />
            </Box>
          )}
          {dialogFormFields.map((field: FormField) => {
            if (field.isAdvanced && !showAdvanced) {
              return null;
            }

            return getDialogFormField(field);
          })}
        </DialogContent>
        <DialogActions
          sx={{ paddingRight: '24px', paddingBottom: '16px', gap: '8px' }}
        >
          <Button id="cancel" variant="outlined" onClick={handleDialogClose}>
            Cancel
          </Button>
          {(dialogType === DialogFor.CONFIRM_PDF_INGESTION ||
            dialogType === DialogFor.CONFIRM_DOC_DOCX_INGESTION ||
            dialogType === DialogFor.CONFIRM_AUDIO_INGESTION ||
            dialogType === DialogFor.CONFIRM_IMAGE_INGESTION ||
            dialogType === DialogFor.ADD_RECURSIVE_URL_MEMORY) &&
            !isProPlan && (
              <Button
                id="upgrade"
                variant="contained"
                color="primary"
                onClick={handleUpgrade}
              >
                Upgrade
              </Button>
            )}
          <Button
            id="submit"
            variant="contained"
            color="primary"
            onClick={() => {
              if (!validateDialogFields()) {
                return;
              }

              if (dialogType) {
                updateDialogueState('ACCEPT');
              }
            }}
            disabled={isSubmitDisabled}
          >
            Submit
          </Button>
        </DialogActions>
      </>
    );
  };

  return (
    <Dialog
      open={dialogueState === 'OPEN' || dialogueState === 'LOADING'}
      onClose={handleDialogClose}
      className="hanabi-scrollbar"
      PaperProps={{ sx: dialogStyle }}
    >
      <DialogTitle
        sx={{
          fontSize: '24px',
          textTransform: 'capitalize',
        }}
      >
        <Typography sx={{ marginBottom: '12px' }} component="p" variant="h5">
          {t(dialogTitle)}
        </Typography>
        <CustomDivider orientation="horizontal" />
      </DialogTitle>
      {getDialogueContent()}
    </Dialog>
  );
});

export default CustomDialog;
